import React, { useState, useCallback, useMemo, useContext } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainer";
import { useQuery, useQueryClient } from "react-query";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Spin } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import { deleteUser, getDrivers } from "../services/userApi";
import "../../Styles/style.css"
import AddEditUserForm from "../component/AddEditUser";
import DetailUser from "../component/DetailUser";

const Users = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();

  document.title = `Utilisateurs | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetFullName, setDeleteTargetFullName] = useState("");
  
  const { isLoading, isError, error, data: user } = useQuery("users", getDrivers);

  const toggleAddEditModal = useCallback((user = null) => {
    setSelectedUser(user);
    setIsAddEditModalOpen(prev => !prev);
    if (isDetailModalOpen) setIsDetailModalOpen(false);
  }, [isDetailModalOpen]);

  const handleEdit = (user) => {
    toggleAddEditModal(user);
  };

  const toggleDetailModal = useCallback((user = null) => {
    setSelectedUser(user);
    setIsDetailModalOpen(prev => !prev);
    if (isAddEditModalOpen) setIsAddEditModalOpen(false);
  }, [isAddEditModalOpen]);

  const handleDetail = (user) => {
    toggleDetailModal(user);
  };

  const handleDelete = async (userId) => {
    try {
      await deleteUser(userId);
      queryClient.invalidateQueries("user");
      toast.success("Le chauffeur a été supprimée avec succès 🎉");
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(`😔 Erreur lors de la suppression du chauffeur : ${error?.message||error}`);
    } finally {
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (userId) => {
    const userToDelete = user.find(user => user.id === userId);
    if (userToDelete) {
      setDeleteTargetId(userId);
      setDeleteTargetFullName(userToDelete.firstName+" "+userToDelete.lastName);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };

  const columns = useMemo(() => [
    { Header: "Prénom", accessor: "firstName", filterable: false },
    { Header: "Nom", accessor: "lastName", filterable: false },   
    {
      Header: "Adresse email",
      accessor: "email",
      Cell: ({ row }) => (
        <div>{row.original.email || "-"}</div>
      ),
      filterable: false,
    },
    { 
      Header: "Téléphone",
      accessor: "phone",
      Cell: ({ row }) => (
      <div>{row.original.phone || "-"}</div>),
      filterable: false 
    },
    { 
      Header: "Adresse",
      accessor: "address",
      Cell: ({ row }) => (
      <div>{row.original.address || "-"}</div>),
      filterable: false 
    },
    { 
      Header: "Role",
      accessor: "role",
      Cell: ({ row }) => (
        <div>{row.original.role.name}</div>),
      filterable: false 
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button
            title="Détail"
            size="sm"
            onClick={() => handleDetail(row.original)}
            className="btn btn-soft-info"
          >
            <EyeOutlined />
          </Button>
          <Button
            title="Modifier"
            size="sm"
            onClick={() => handleEdit(row.original)}
            className="btn btn-soft-warning"
          >
            <EditOutlined />
          </Button>
          <Button
            title="Supprimer"
            size="sm"
            onClick={() => openDeleteConfirmModal(row.original.id)}
            className="btn btn-soft-danger"
          >
            <DeleteOutlined />
          </Button>
        </div>
      ),
      filterable: false,
    },
  ], [handleDetail, handleEdit, openDeleteConfirmModal]);


  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3">
        Liste des utilisateur de Fleet Fix
      </div>
      
      <Button
        type="button"
        className="btn btn-soft-success"
        icon={<PlusCircleOutlined />}
        title="Ajout utilisateur"
        onClick={() => toggleAddEditModal(null)}
        >
          Ajouter un utilisateur
      </Button>
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="utilisateurs" history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainerMin
                    columns={columns}
                    data={user || []}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={5}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    SearchPlaceholder="Recherche..."
                    HeaderTable={HeaderTable}
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      Chargement, Merci de patienter ...  <Spin />
                    </div>
                  )}
                  {isError && (
                    <div className="text-center">
                      Erreur : {error?.message || error}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          <AddEditUserForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedUser={selectedUser}
          />
          <DetailUser
            modal={isDetailModalOpen}
            setModal={setIsDetailModalOpen}
            toggle={toggleDetailModal}
            selectedUser={selectedUser}
          />
          <Modal
            title="Confirmer la suppression de cet utilisateur"
            open={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={[
              <Button key="cancel" onClick={closeDeleteConfirmModal} className="btn btn-soft-primary">
                Annuler
              </Button>,
              <Button key="delete" type="danger" className="btn btn-soft-danger" onClick={() => handleDelete(deleteTargetId)}>
                Oui, Supprime
              </Button>,
            ]}
          >
            <p>Êtes-vous sûr de vouloir supprimer le chauffeur {deleteTargetFullName} ?</p>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Users;
